import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/terms")({
  component: TermsPage,
  head: () => ({
    meta: [
      { title: "Terms & Conditions | Spirit Science Summit" },
      {
        name: "description",
        content:
          "Terms & Conditions for the Spirit Science Summit on September 12, 2026 in Davenport, Florida.",
      },
    ],
  }),
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="text-xl font-semibold text-foreground">{title}</h2>
      <div className="mt-3 space-y-3 text-muted-foreground leading-relaxed">{children}</div>
    </section>
  );
}

function TermsPage() {
  return (
    <main className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-6 py-16">
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
          ← Back to home
        </Link>

        <h1 className="mt-6 text-4xl font-bold text-foreground">Terms & Conditions</h1>

        <div className="mt-6 space-y-1 text-sm text-muted-foreground">
          <p><span className="text-foreground font-medium">Effective Date:</span> May 21, 2026</p>
          <p><span className="text-foreground font-medium">Website:</span> spiritsciencesummit.com</p>
          <p><span className="text-foreground font-medium">Event:</span> Integrating Well-being, Spirituality, Spiritism & Science</p>
          <p><span className="text-foreground font-medium">Event Date:</span> September 12, 2026</p>
          <p><span className="text-foreground font-medium">Location:</span> Tom Fellows Community Center, 207 North Boulevard West, Davenport, FL 33837</p>
          <p>
            <span className="text-foreground font-medium">Contact:</span>{" "}
            <a href="mailto:info@spiritsciencesummit.com" className="underline hover:text-foreground">
              info@spiritsciencesummit.com
            </a>
          </p>
        </div>

        <Section title="1. Acceptance of Terms">
          <p>
            By accessing this website, purchasing a ticket, registering for the event, or attending
            the event, you agree to these Terms & Conditions. If you do not agree with these terms,
            please do not use this website or purchase a ticket.
          </p>
        </Section>

        <Section title="2. Event Information">
          <p>
            The Integrating Well-being, Spirituality, Spiritism & Science Summit is a full-day
            educational and inspirational event focused on well-being, spirituality, Spiritism,
            consciousness studies, and science.
          </p>
          <p>
            The event is scheduled for September 12, 2026, at Tom Fellows Community Center in
            Davenport, Florida. The program may include lectures, panel discussions, audience Q&A,
            lunch, and simultaneous Portuguese translation.
          </p>
          <p>
            We reserve the right to update event details, including agenda, speakers, schedule,
            venue logistics, translation services, lunch arrangements, and other event-related
            information when necessary.
          </p>
        </Section>

        <Section title="3. Tickets and Registration">
          <p>
            Tickets must be purchased through the official ticketing link provided on this website.
            Your ticket grants you access to the event according to the ticket type purchased.
          </p>
          <p>
            Tickets may include access to the full-day event, keynote sessions, panel discussion,
            Portuguese translation, and lunch, as described on the ticket page.
          </p>
          <p>
            Seating is limited, and admission is subject to venue capacity and successful ticket
            confirmation.
          </p>
        </Section>

        <Section title="4. Ticket Transfers">
          <p>
            Tickets may be transferred to another person if you are unable to attend, provided the
            transfer request is submitted to us in writing at info@spiritsciencesummit.com no later
            than September 5, 2026.
          </p>
          <p>
            The new attendee's full name and email address must be provided. We reserve the right
            to deny transfer requests submitted after the deadline or requests that appear
            fraudulent or incomplete.
          </p>
        </Section>

        <Section title="5. Event Changes, Postponement, or Cancellation">
          <p>
            While we make every reasonable effort to deliver the event as announced, unforeseen
            circumstances may require changes. These may include speaker availability, venue
            requirements, weather, public safety concerns, technical issues, or other circumstances
            beyond our control.
          </p>
          <p>
            If the event is postponed, your ticket will remain valid for the rescheduled date. If
            the event is canceled and not rescheduled, refund options will be provided according to
            our Refund Policy.
          </p>
          <p>
            We are not responsible for travel, lodging, transportation, parking, childcare, lost
            income, or any other personal expenses related to attending the event.
          </p>
        </Section>

        <Section title="6. Speaker Views and Educational Purpose">
          <p>
            The opinions, teachings, research, and perspectives shared by speakers are their own
            and do not necessarily represent the official views of the event organizers, sponsors,
            partners, volunteers, or venue.
          </p>
          <p>
            The event is for educational, cultural, spiritual, and inspirational purposes only.
            Nothing presented at the event should be considered medical, psychological, psychiatric,
            legal, financial, or professional advice.
          </p>
        </Section>

        <Section title="7. Health, Wellness, and Personal Responsibility">
          <p>
            Some sessions may discuss well-being, spirituality, mental health, consciousness,
            healing, or related topics. These discussions are not intended to diagnose, treat, cure,
            or prevent any disease or condition.
          </p>
          <p>
            Always consult a qualified healthcare professional regarding medical, mental health,
            nutritional, or therapeutic concerns. Your participation in the event is voluntary and
            at your own discretion.
          </p>
        </Section>

        <Section title="8. Food, Allergies, and Dietary Needs">
          <p>
            Lunch may be included as part of the ticket, as described on the website. Vegetarian
            options may be available.
          </p>
          <p>
            Attendees are responsible for notifying the organizers of any dietary restrictions or
            allergies in advance. While we will make reasonable efforts to accommodate dietary
            needs, we cannot guarantee that food will be free from allergens or cross-contamination.
          </p>
        </Section>

        <Section title="9. Photography, Video, and Media Release">
          <p>
            By attending the event, you acknowledge that photography, video recording, or audio
            recording may take place. You grant the organizers permission to use event photos,
            videos, and recordings that may include your image, likeness, or voice for promotional,
            educational, archival, or marketing purposes.
          </p>
          <p>
            If you do not wish to be photographed or recorded, please notify the event staff upon
            arrival. We will make reasonable efforts to accommodate your request, but we cannot
            guarantee complete exclusion from all crowd or background images.
          </p>
        </Section>

        <Section title="10. Attendee Conduct">
          <p>
            All attendees are expected to behave respectfully toward speakers, staff, volunteers,
            venue personnel, and other participants.
          </p>
          <p>
            We reserve the right to remove any attendee from the event without refund if they
            engage in disruptive, abusive, threatening, discriminatory, unsafe, or inappropriate
            behavior.
          </p>
        </Section>

        <Section title="11. Website Use">
          <p>
            You agree not to misuse this website, attempt unauthorized access, interfere with
            website operation, copy content without permission, or use the website for unlawful
            purposes.
          </p>
          <p>
            All website content, including text, graphics, images, branding, layout, and event
            materials, is owned by or licensed to the organizers unless otherwise stated.
          </p>
        </Section>

        <Section title="12. Limitation of Liability">
          <p>
            To the fullest extent permitted by law, the organizers, volunteers, speakers, partners,
            sponsors, and venue shall not be liable for any indirect, incidental, consequential,
            special, or punitive damages arising from your use of the website, ticket purchase, or
            attendance at the event.
          </p>
          <p>
            Your sole remedy for dissatisfaction with the event or website is to stop using the
            website or, where applicable, request a refund according to the Refund Policy.
          </p>
        </Section>

        <Section title="13. Governing Law">
          <p>
            These Terms & Conditions shall be governed by the laws of the State of Florida, without
            regard to conflict of law principles.
          </p>
        </Section>

        <Section title="14. Contact">
          <p>For questions about these Terms & Conditions, please contact:</p>
          <p>
            <a href="mailto:info@spiritsciencesummit.com" className="underline hover:text-foreground">
              info@spiritsciencesummit.com
            </a>
          </p>
        </Section>
      </div>
    </main>
  );
}
